/*#
# IndexedDB log

`IndexedDBLog` is the browser-persistent [sync log](/log/): one object store,
`seq` as an auto-incremented key. It is the local half of the architecture —
the client's cached working set — not a stand-in for the server. When the
service-compris transport lands, this store keeps the replayed log so the
board opens instantly and keeps working read-only if the service is down.

`close()` is terminal: every operation after it rejects with `ClosedLogError`
(the name is on every message, so an error log is self-explanatory). When
another tab deletes or upgrades the database, this log's connection is
closed the same way (`onversionchange`) — the board above it treats that as
a withdrawn replica and rebuilds, never as an outage.

```js
import { IndexedDBLog, VirtaStore } from 'tosijs-virta'

const log = new IndexedDBLog('virta-doc-demo')
await log.clear()
const store = new VirtaStore({ log, identity: 'demo' })
await store.load()
await store.create({ title: 'persisted across reloads', tags: ['demo'] })

// A second store over the same database sees the event after replay.
const again = new VirtaStore({ log: new IndexedDBLog('virta-doc-demo'), identity: 'demo' })
await again.load()
preview.textContent = again.tasks.map((t) => `#${t.num} ${t.title}`).join('\n')
```
```test
test('a fresh store replays what the first one wrote', () => {
  expect(preview.textContent).toBe('#1 persisted across reloads')
})
```
*/

/*{ "parent": "Reference", "order": 45 }*/

import type { EventDraft, VirtaEvent } from './model'
import { DEFAULT_PAGE, type LogPage, type SyncLog } from './log'

const STORE = 'events'

function request<T>(req: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    req.onsuccess = () => resolve(req.result)
    req.onerror = () => reject(req.error)
  })
}

/**
 * Add, or — if another connection added the same id between our lookup and
 * our add — swallow the ConstraintError (keeping the transaction alive) and
 * return the stored event instead.
 */
function addOrExisting(
  store: IDBObjectStore,
  draft: EventDraft
): Promise<number | VirtaEvent> {
  return new Promise((resolve, reject) => {
    const req = store.add(draft)
    req.onsuccess = () => resolve(req.result as number)
    req.onerror = (event) => {
      if (req.error?.name !== 'ConstraintError') {
        reject(req.error)
        return
      }
      event.preventDefault()
      event.stopPropagation()
      const again = store.index('id').get(draft.id)
      again.onsuccess = () => resolve(again.result as VirtaEvent)
      again.onerror = () => reject(again.error)
    }
  })
}

function done(tx: IDBTransaction): Promise<void> {
  return new Promise((resolve, reject) => {
    tx.oncomplete = () => resolve()
    tx.onerror = () => reject(tx.error)
    tx.onabort = () => reject(tx.error)
  })
}

/** Thrown by every operation on a log after `close()`: the log is gone, and nothing re-creates it. */
export class ClosedLogError extends Error {
  constructor(name: string) {
    super(`the IndexedDB log ${name} is closed`)
    this.name = 'ClosedLogError'
  }
}

export class IndexedDBLog implements SyncLog {
  private db: Promise<IDBDatabase> | null = null
  /** Set by `close()`, never unset: a closed log is never reopened (0.5.0 re-review 4, B2). */
  private closed = false

  constructor(public readonly name = 'tosijs-virta') {}

  private open(): Promise<IDBDatabase> {
    if (this.closed) return Promise.reject(new ClosedLogError(this.name))
    if (!this.db) {
      const req = indexedDB.open(this.name, 2)
      req.onupgradeneeded = () => {
        const db = req.result
        const store = db.objectStoreNames.contains(STORE)
          ? req.transaction!.objectStore(STORE)
          : db.createObjectStore(STORE, { keyPath: 'seq', autoIncrement: true })
        // Event ids are minted by writers and globally unique, so a second push
        // of the same event (a retry, two tabs seeding at once) must not append.
        // A v1 database may already hold a duplicated id (nothing forbade it);
        // creating a unique index over it would abort the upgrade and the
        // board would never open, so drop later duplicates first.
        if (!store.indexNames.contains('id')) {
          const seen = new Set<string>()
          const cursor = store.openCursor()
          cursor.onsuccess = () => {
            const c = cursor.result
            if (!c) {
              store.createIndex('id', 'id', { unique: true })
              return
            }
            const id = (c.value as { id?: string }).id ?? ''
            if (seen.has(id)) c.delete()
            else seen.add(id)
            c.continue()
          }
        }
      }
      const opening = request(req).then((db) => {
        // another tab is deleting or upgrading this database: let go of it
        // at once (a held connection would block that delete forever) and
        // stay closed — whatever replaced it is not this log's
        db.onversionchange = () => {
          db.close()
          this.db = null
          this.closed = true
        }
        return db
      })
      // a failed open is not this log's fate: forget it, so the next call
      // tries again instead of failing forever (board #50)
      opening.catch(() => {
        if (this.db === opening) this.db = null
      })
      this.db = opening
    }
    return this.db
  }

  async pull(after: number, limit = DEFAULT_PAGE): Promise<LogPage> {
    const db = await this.open()
    const tx = db.transaction(STORE, 'readonly')
    const range = IDBKeyRange.lowerBound(after, true)
    const events = (await request(
      tx.objectStore(STORE).getAll(range, limit)
    )) as VirtaEvent[]
    const last = events[events.length - 1]
    return { events, cursor: last ? last.seq : after }
  }

  async push(drafts: EventDraft[]): Promise<VirtaEvent[]> {
    const db = await this.open()
    const tx = db.transaction(STORE, 'readwrite')
    // Listen for completion before issuing requests: a transaction commits as
    // soon as its last request settles, so attaching afterwards can miss it.
    const finished = done(tx)
    const store = tx.objectStore(STORE)
    const byId = store.index('id')
    // Idempotent by id: an event already in the log is returned as stored, not appended.
    const out: VirtaEvent[] = []
    for (const draft of drafts) {
      const existing = (await request(byId.get(draft.id))) as
        | VirtaEvent
        | undefined
      if (existing) {
        out.push(existing)
        continue
      }
      const seq = await addOrExisting(store, draft)
      if (typeof seq === 'number') out.push({ ...draft, seq } as VirtaEvent)
      else out.push(seq)
    }
    await finished
    return out
  }

  /**
   * As a replica of a host: store events with the sequence numbers the host
   * assigned (the key path is `seq`, and an explicit key wins over the
   * auto-increment). An id already held is skipped; an out-of-order seq is
   * refused, since a replica's order is the host's order or it is nothing.
   */
  async append(events: VirtaEvent[]): Promise<void> {
    if (events.length === 0) return
    const db = await this.open()
    const tx = db.transaction(STORE, 'readwrite')
    const finished = done(tx)
    const store = tx.objectStore(STORE)
    const byId = store.index('id')
    let tail = await this.lastSeq(store)
    for (const event of events) {
      const existing = await request(byId.getKey(event.id))
      if (existing !== undefined) continue
      if (event.seq <= tail) {
        // our error is the one to surface; the abort's own rejection is noise
        finished.catch(() => undefined)
        tx.abort()
        throw new Error(
          `virta: cannot append seq ${event.seq} after ${tail} (out of order)`
        )
      }
      await request(store.add(event))
      tail = event.seq
    }
    await finished
  }

  async tail(): Promise<number> {
    const db = await this.open()
    const tx = db.transaction(STORE, 'readonly')
    return this.lastSeq(tx.objectStore(STORE))
  }

  private async lastSeq(store: IDBObjectStore): Promise<number> {
    const cursor = (await request(
      store.openKeyCursor(null, 'prev')
    )) as IDBCursor | null
    return cursor ? Number(cursor.key) : 0
  }

  /** Drop every event. For demos and tests; the real log is append-only. */
  async clear(): Promise<void> {
    const db = await this.open()
    const tx = db.transaction(STORE, 'readwrite')
    const finished = done(tx)
    tx.objectStore(STORE).clear()
    await finished
  }

  /**
   * Terminal: after this every operation rejects with `ClosedLogError`. A
   * sync still in flight when the replica is being torn down must not
   * re-create the database with the old credential's events — `open()` is
   * lazy, so without this flag its next append would.
   */
  async close(): Promise<void> {
    this.closed = true
    const db = this.db
    this.db = null
    if (!db) return
    try {
      ;(await db).close()
    } catch {
      /* never opened successfully: nothing to close */
    }
  }
}
